
const HowItWorks = () => {
  const steps = [
    {
      step: "01",
      title: "Free Consultation",
      description: "Our solar experts visit your home to assess your energy needs, roof condition, and provide personalized recommendations.",
      icon: "ri-user-search-line",
      color: "emerald",
      details: [
        "Site assessment and energy audit",
        "Roof analysis and shading study",
        "Customized system design",
        "Cost-benefit analysis"
      ]
    },
    {
      step: "02",
      title: "Subsidy Application",
      description: "We handle all paperwork and guide you through the PM Surya Ghar Yojana subsidy application process.",
      icon: "ri-file-text-line",
      color: "blue",
      details: [
        "Document preparation assistance",
        "Online application submission",
        "Subsidy approval tracking",
        "Government liaison support"
      ]
    },
    {
      step: "03",
      title: "Professional Installation",
      description: "Certified technicians install your solar system safely and efficiently, ensuring optimal performance.",
      icon: "ri-tools-line",
      color: "amber",
      details: [
        "Quality component installation",
        "Safety compliance checks",
        "Grid connection setup",
        "System testing and commissioning"
      ]
    },
    {
      step: "04",
      title: "Start Saving",
      description: "Begin generating clean energy immediately and enjoy reduced electricity bills with ongoing support.",
      icon: "ri-money-rupee-circle-line",
      color: "green",
      details: [
        "Immediate power generation",
        "Monthly savings tracking",
        "Performance monitoring",
        "Maintenance support"
      ]
    }
  ];

  const colorClasses = {
    emerald: {
      bg: "from-emerald-500 to-emerald-600",
      light: "bg-emerald-50",
      text: "text-emerald-600",
      border: "border-emerald-200"
    },
    blue: {
      bg: "from-blue-500 to-blue-600",
      light: "bg-blue-50",
      text: "text-blue-600",
      border: "border-blue-200"
    },
    amber: {
      bg: "from-amber-500 to-amber-600",
      light: "bg-amber-50",
      text: "text-amber-600",
      border: "border-amber-200"
    },
    green: {
      bg: "from-green-500 to-green-600",
      light: "bg-green-50",
      text: "text-green-600",
      border: "border-green-200"
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-roadmap-line mr-2"></i>
            How It Works
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Your Journey to
            <span className="block text-transparent bg-gradient-to-r from-emerald-600 to-amber-500 bg-clip-text">
              Solar Independence
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From consultation to installation, we make going solar simple and hassle-free. 
            Here's how we transform your home into a clean energy powerhouse.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {steps.map((step, index) => {
              const colors = colorClasses[step.color];
              const isEven = index % 2 === 1;
              
              return (
                <div
                  key={index}
                  className={`relative ${isEven ? 'lg:order-2' : ''}`}
                >
                  {/* Connection Line */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-6 w-12 h-0.5 bg-gradient-to-r from-emerald-300 to-amber-300"></div>
                  )}
                  
                  <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                    <div className="flex items-start space-x-6">
                      {/* Step Number */}
                      <div className={`flex-shrink-0 w-16 h-16 bg-gradient-to-r ${colors.bg} rounded-2xl flex items-center justify-center shadow-lg`}>
                        <span className="text-white font-bold text-lg">{step.step}</span>
                      </div>

                      <div className="flex-1">
                        {/* Icon and Title */}
                        <div className="flex items-center mb-4">
                          <div className={`w-12 h-12 ${colors.light} rounded-xl flex items-center justify-center mr-4`}>
                            <i className={`${step.icon} ${colors.text} text-xl`}></i>
                          </div>
                          <h3 className="text-2xl font-bold text-gray-900">{step.title}</h3>
                        </div>

                        {/* Description */}
                        <p className="text-gray-600 leading-relaxed mb-6">
                          {step.description}
                        </p>

                        {/* Details */}
                        <div className={`${colors.light} rounded-xl p-4`}>
                          <h4 className={`${colors.text} font-semibold mb-3 text-sm uppercase tracking-wide`}>
                            What's Included:
                          </h4>
                          <ul className="space-y-2">
                            {step.details.map((detail, detailIndex) => (
                              <li key={detailIndex} className="flex items-center text-sm text-gray-700">
                                <div className={`w-4 h-4 ${colors.bg} rounded-full flex items-center justify-center mr-3 flex-shrink-0`}>
                                  <i className="ri-check-line text-white text-xs"></i>
                                </div>
                                {detail}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Timeline Visual */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 shadow-xl">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
              Typical Installation Timeline
            </h3>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-emerald-600 font-bold">1</span>
                </div>
                <div className="font-semibold text-gray-900 mb-1">Day 1-2</div>
                <div className="text-sm text-gray-600">Site Visit & Assessment</div>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-blue-600 font-bold">2</span>
                </div>
                <div className="font-semibold text-gray-900 mb-1">Day 3-13</div>
                <div className="text-sm text-gray-600">Installation & Setup</div>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-amber-600 font-bold">3</span>
                </div>
                <div className="font-semibold text-gray-900 mb-1">Day 14-16 </div>
                <div className="text-sm text-gray-600">Subsidy Processing</div>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-green-600 font-bold">4</span>
                </div>
                <div className="font-semibold text-gray-900 mb-1">Day 16+</div>
                <div className="text-sm text-gray-600">Start Generating Power</div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-emerald-600 to-amber-500 rounded-3xl p-8 text-white max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to Start Your Solar Journey?
            </h3>
            <p className="text-emerald-100 mb-6 text-lg">
              Get a free consultation today and take the first step towards energy independence
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:7496038344"
                className="inline-flex items-center bg-white text-emerald-600 px-8 py-4 rounded-full font-semibold hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg"
              >
                <i className="ri-phone-line mr-2"></i>
                Call Now: 7496038344
              </a>
              <a
                href="#contact"
                className="inline-flex items-center border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-emerald-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-message-line mr-2"></i>
                Get Free Quote
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
